// src/MalaysiaApp.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { RequirementsOverview } from './RequirementsOverview';
import { DocumentUpload } from './DocumentUpload';
import { PassportScanner } from './PassportScanner';
import { SearchableSelect } from './SearchableSelect';
import { ShareDraftModal } from './ShareDraftModal';
import { SaveResumeModal } from './SaveResumeModal';
import { saveSubmittedApplication } from './data/applicationsStore';
import { loadFormProgressAsync, SavedFormState } from './data/resumeStore';
import { loadAdminConfig, loadAdminConfigAsync, AdminStoreConfig, getQuestionsForCountry } from './data/adminStore';
import { COUNTRIES } from './questions';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';
const YOUR_EMAIL = process.env.REACT_APP_YOUR_EMAIL || 'myskydestinations@gmail.com';

const MALAYSIA_STEP_TITLES = [
  { step: 1, title: 'Checklist & Fees', icon: '📑' },
  { step: 2, title: 'Personal & Passport', icon: '👤' },
  { step: 3, title: 'Flight & Hotel', icon: '✈️' },
  { step: 4, title: 'Document Upload (Optional)', icon: '📁' },
];

export const MalaysiaApp: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const [adminConfig, setAdminConfig] = useState<AdminStoreConfig>(loadAdminConfig);

  useEffect(() => {
    loadAdminConfigAsync().then((conf) => setAdminConfig(conf));
    const handleSync = () => setAdminConfig(loadAdminConfig());
    window.addEventListener('adminConfigUpdated', handleSync);
    return () => window.removeEventListener('adminConfigUpdated', handleSync);
  }, []);

  const dynamicQuestions = getQuestionsForCountry(adminConfig, 'MALAYSIA');
  const getQuestion = (id: string) => dynamicQuestions.find((q) => q.id === id);

  const categoryOptions = getQuestion('category')?.options || [
    'e-VISA Tourist (30 Days - Single Entry)',
    'e-VISA Tourist (30 Days - Multiple Entry)',
    'e-VISA Expatriate',
  ];

  const [formData, setFormData] = useState({
    category: 'e-VISA Tourist (30 Days - Single Entry)',
    surname: '',
    givenName: '',
    passportNumber: '',
    nationality: 'SRI LANKA',
    gender: 'MALE',
    dob: '',
    passportExpiry: '',
    flightArrivalDate: '',
    flightDepartureDate: '',
    airline: '',
    flightNumber: '',
    malaysiaHotelAddress: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resumeCode = params.get('resume');
    if (resumeCode) {
      loadFormProgressAsync(resumeCode).then((saved) => {
        if (saved && saved.formValues) {
          setFormData((prev) => ({ ...prev, ...saved.formValues }));
          if (saved.step) setStep(saved.step);
        }
      });
    }
  }, []);

  const handleChange = (field: string, val: string) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleAutoFillMRZ = (data: any) => {
    setFormData((prev) => ({
      ...prev,
      surname: data.surname || prev.surname,
      givenName: data.givenName || prev.givenName,
      passportNumber: data.passportNumber || prev.passportNumber,
      dob: data.dobApplicant || prev.dob,
      passportExpiry: data.passportDateExpiry || prev.passportExpiry,
      gender: data.gender || prev.gender,
    }));
  };

  const handleRestoreFormState = (saved: SavedFormState) => {
    if (saved.formValues) {
      setFormData((prev) => ({ ...prev, ...saved.formValues }));
    }
    if (saved.step) {
      setStep(saved.step);
    }
  };

  const validateStep = (currentStep: number): boolean => {
    if (currentStep === 2) {
      if (!formData.surname.trim()) {
        alert('⚠️ Please enter Surname (as in Passport)');
        return false;
      }
      if (!formData.givenName.trim()) {
        alert('⚠️ Please enter Given Name/s (as in Passport)');
        return false;
      }
      if (!formData.passportNumber.trim()) {
        alert('⚠️ Please enter Passport Number');
        return false;
      }
      if (!formData.dob) {
        alert('⚠️ Please select Date of Birth');
        return false;
      }
      if (!formData.passportExpiry) {
        alert('⚠️ Please select Passport Expiry Date');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!formData.flightArrivalDate) {
        alert('⚠️ Please select Flight Arrival Date');
        return false;
      }
      if (!formData.flightDepartureDate) {
        alert('⚠️ Please select Flight Departure Date');
        return false;
      }
      if (!formData.airline.trim()) {
        alert('⚠️ Please enter Airline Name');
        return false;
      }
      if (!formData.flightNumber.trim()) {
        alert('⚠️ Please enter Flight Number');
        return false;
      }
      if (!formData.malaysiaHotelAddress.trim()) {
        alert('⚠️ Please enter Intended Accommodation / Hotel Address');
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep((s) => Math.min(s + 1, 4));
    }
  };

  const handleSubmit = async () => {
    setIsSending(true);
    const refId = `MYS-MYS-${Math.floor(10000 + Math.random() * 90000)}`;

    let emailSentSuccess = false;
    if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
      try {
        const message = Object.entries(formData)
          .map(([key, val]) => `${key}: ${val}`)
          .join('\n\n');

        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            to_email: YOUR_EMAIL,
            from_name: `${formData.givenName} ${formData.surname}`.trim() || 'Malaysia Applicant',
            reply_to: YOUR_EMAIL,
            message,
          },
          EMAILJS_PUBLIC_KEY
        );
        emailSentSuccess = true;
      } catch (err) {
        console.warn('EmailJS sending warning:', err);
      }
    }

    saveSubmittedApplication({
      id: refId,
      destination: 'MALAYSIA',
      countryName: 'Malaysia',
      applicantName: `${formData.givenName || ''} ${formData.surname || ''}`.trim() || 'Applicant',
      passportNumber: formData.passportNumber || 'N/A',
      nationality: formData.nationality || 'Sri Lanka',
      visaCategory: formData.category || 'e-VISA Tourist',
      status: 'Pending 🟡',
      submittedAt: new Date().toLocaleString(),
      totalFeeLkr: 24500,
      data: formData,
    });

    if (emailSentSuccess) {
      alert(`✅ Malaysia e-Visa Application Submitted Successfully!\n\nReference: ${refId}\nEmail notification sent to ${YOUR_EMAIL}.`);
    } else {
      alert(`✅ Malaysia e-Visa Application Submitted Successfully!\n\nReference: ${refId}.`);
    }

    setIsSending(false);
    navigate('/review?country=MALAYSIA');
  };

  const progressPercent = Math.round(((step - 1) / (MALAYSIA_STEP_TITLES.length - 1)) * 100);

  return (
    <div style={{ maxWidth: '940px', margin: '0 auto', padding: '1.5rem 1rem 5rem' }}>
      {/* HEADER BRANDING */}
      <header
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0284c7 100%)',
          borderRadius: '16px',
          padding: '2rem 1.75rem',
          color: '#ffffff',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.35)',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(8px)',
                  padding: '0.35rem 0.85rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                🇲🇾 Malaysia e-VISA Helper
              </span>
            </div>
            <h1 style={{ fontSize: '1.85rem', fontWeight: '800', marginTop: '0.5rem', margin: 0 }}>
              Malaysia e-VISA Application Form
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '0.25rem', opacity: 0.9 }}>
              Exclusively designed for <strong>mySky Travels</strong> by Madushka Athapaththu
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                color: '#0f172a',
                border: '1px solid #fef08a',
                padding: '0.55rem 1rem',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                boxShadow: '0 4px 14px rgba(217, 119, 6, 0.45)',
                transition: 'all 0.15s ease',
              }}
              title="Return to mySky Destinations Hub"
            >
              🏠 Destinations Hub
            </button>

            <button
              type="button"
              onClick={() => setShowShareModal(true)}
              style={{
                background: 'rgba(59, 130, 246, 0.25)',
                border: '1px solid rgba(59, 130, 246, 0.5)',
                color: '#93c5fd',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              📲 Share Draft
            </button>

            <button
              type="button"
              onClick={() => setShowSaveModal(true)}
              style={{
                background: 'rgba(16, 185, 129, 0.25)',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                color: '#34d399',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              💾 Save
            </button>

            <button
              type="button"
              onClick={() => setShowResumeModal(true)}
              style={{
                background: 'rgba(99, 102, 241, 0.25)',
                border: '1px solid rgba(99, 102, 241, 0.5)',
                color: '#a5b4fc',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              🔑 Resume
            </button>

            <div
              style={{
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '10px',
                padding: '0.45rem 0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                userSelect: 'none',
              }}
              title="Form Completion Progress"
            >
              <span style={{ fontSize: '0.725rem', color: '#cbd5e1', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Progress</span>
              <span style={{ fontSize: '0.95rem', fontWeight: '900', color: '#38bdf8', background: 'rgba(56, 189, 248, 0.2)', padding: '0.15rem 0.55rem', borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.4)' }}>
                {progressPercent}%
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar Line */}
        <div
          style={{
            width: '100%',
            height: '8px',
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '9999px',
            marginTop: '1.5rem',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #38bdf8 0%, #34d399 100%)',
              transition: 'width 0.4s ease-in-out',
              borderRadius: '9999px',
            }}
          />
        </div>
      </header>

      {/* STEPPER NAVIGATION HEADER */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.5rem',
          marginBottom: '2rem',
        }}
      >
        {MALAYSIA_STEP_TITLES.map((st) => {
          const isActive = st.step === step;
          const isCompleted = st.step < step;

          return (
            <button
              key={st.step}
              type="button"
              onClick={() => {
                if (isCompleted) setStep(st.step);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 0.85rem',
                borderRadius: '10px',
                border: isActive
                  ? '2px solid #0284c7'
                  : isCompleted
                  ? '1px solid #10b981'
                  : '1px solid #e2e8f0',
                background: isActive ? '#f0f9ff' : isCompleted ? '#f0fdf4' : '#ffffff',
                color: isActive ? '#0369a1' : isCompleted ? '#047857' : '#64748b',
                cursor: isCompleted ? 'pointer' : 'default',
                fontWeight: isActive ? '700' : '500',
                fontSize: '0.85rem',
                textAlign: 'left',
                boxShadow: isActive ? '0 4px 12px rgba(2, 132, 199, 0.15)' : 'none',
              }}
            >
              <span
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: isActive ? '#0284c7' : isCompleted ? '#10b981' : '#e2e8f0',
                  color: isActive || isCompleted ? '#ffffff' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  flexShrink: 0,
                }}
              >
                {isCompleted ? '✓' : st.step}
              </span>
              <span style={{ flex: 1 }}>{st.icon} {st.title}</span>
            </button>
          );
        })}
      </div>

      {/* STEP 1: REQUIREMENTS & CHECKLIST */}
      {step === 1 && (
        <RequirementsOverview
          countryCode="MALAYSIA"
          onStartApplication={() => setStep(2)}
        />
      )}

      {/* WIZARD STEPS */}
      {step > 1 && (
        <div className="card-container animate-fade-in">
          {step === 2 && (
            <div>
              <h2 style={{ fontSize: '1.3rem', color: '#0f172a', fontWeight: '800', marginBottom: '1.25rem', borderBottom: '2px solid #0284c7', paddingBottom: '0.5rem' }}>
                Step 2: Applicant Personal & Passport Details
              </h2>

              <PassportScanner onAutoFill={handleAutoFillMRZ} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('category')?.label || '*Select e-VISA Category:'}</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  >
                    {categoryOptions.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Surname (as in Passport):</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => handleChange('surname', e.target.value.toUpperCase())}
                    placeholder="e.g. ATHAPATHTHU"
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Given Name/s (as in Passport):</label>
                  <input
                    type="text"
                    value={formData.givenName}
                    onChange={(e) => handleChange('givenName', e.target.value.toUpperCase())}
                    placeholder="e.g. MADUSHKA HARSHANA"
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Passport Number:</label>
                  <input
                    type="text"
                    value={formData.passportNumber}
                    onChange={(e) => handleChange('passportNumber', e.target.value.toUpperCase())}
                    placeholder="e.g. N12345678"
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Nationality:</label>
                  <SearchableSelect
                    id="malaysia_nationality"
                    options={COUNTRIES}
                    value={formData.nationality}
                    onChange={(val) => handleChange('nationality', val)}
                    placeholder="Select Nationality..."
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Gender:</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  >
                    <option value="MALE">MALE</option>
                    <option value="FEMALE">FEMALE</option>
                    <option value="OTHER">OTHER</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Date of Birth:</label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleChange('dob', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Passport Expiry Date:</label>
                  <input
                    type="date"
                    value={formData.passportExpiry}
                    onChange={(e) => handleChange('passportExpiry', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 style={{ fontSize: '1.3rem', color: '#0f172a', fontWeight: '800', marginBottom: '1.25rem', borderBottom: '2px solid #0284c7', paddingBottom: '0.5rem' }}>
                Step 3: Travel & Flight Itinerary
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Flight Arrival Date in Malaysia:</label>
                  <input
                    type="date"
                    value={formData.flightArrivalDate}
                    onChange={(e) => handleChange('flightArrivalDate', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Flight Departure Date from Malaysia:</label>
                  <input
                    type="date"
                    value={formData.flightDepartureDate}
                    onChange={(e) => handleChange('flightDepartureDate', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Airline Name:</label>
                  <input
                    type="text"
                    value={formData.airline}
                    onChange={(e) => handleChange('airline', e.target.value.toUpperCase())}
                    placeholder="e.g. SriLankan Airlines / Malaysia Airlines"
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Flight Number:</label>
                  <input
                    type="text"
                    value={formData.flightNumber}
                    onChange={(e) => handleChange('flightNumber', e.target.value.toUpperCase())}
                    placeholder="e.g. UL 314"
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>*Intended Accommodation / Hotel Address in Malaysia:</label>
                <textarea
                  rows={3}
                  value={formData.malaysiaHotelAddress}
                  onChange={(e) => handleChange('malaysiaHotelAddress', e.target.value.toUpperCase())}
                  placeholder="e.g. Hilton Kuala Lumpur, 3 Jalan Stesen Sentral, KL"
                  style={{ marginTop: '0.35rem' }}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '2px solid #0284c7', paddingBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h2 style={{ fontSize: '1.3rem', color: '#0f172a', fontWeight: '800', margin: 0 }}>
                  Step 4: Document Upload Helper
                </h2>
                <span style={{ fontSize: '0.8rem', color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '0.35rem 0.75rem', borderRadius: '9999px', fontWeight: '700' }}>
                  ℹ️ Optional Step
                </span>
              </div>
              <DocumentUpload />
            </div>
          )}

          {/* NAVIGATION CONTROLS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button type="button" onClick={() => setStep((s) => Math.max(s - 1, 1))} className="btn-secondary">
              ← Back
            </button>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowSaveModal(true)}
                className="btn-secondary"
                style={{ color: '#059669', borderColor: '#10b981' }}
              >
                💾 Save Draft
              </button>

              {step < 4 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="btn-primary"
                  style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' }}
                >
                  Save & Next →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSending}
                  className="btn-primary"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}
                >
                  {isSending ? 'Sending Email...' : 'Submit Malaysia e-VISA ✅'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <ShareDraftModal
          applicantName={`${formData.givenName || ''} ${formData.surname || ''}`.trim()}
          formData={formData}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showSaveModal && (
        <SaveResumeModal
          mode="SAVE"
          destination="Malaysia"
          step={step}
          formValues={formData}
          onClose={() => setShowSaveModal(false)}
          onRestore={handleRestoreFormState}
        />
      )}

      {showResumeModal && (
        <SaveResumeModal
          mode="RESUME"
          destination="Malaysia"
          step={step}
          onClose={() => setShowResumeModal(false)}
          onRestore={handleRestoreFormState}
        />
      )}
    </div>
  );
};
